import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Collapse,
  Divider,
  LinearProgress,
  Switch,
  FormControlLabel,
  Avatar,
  Popover,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Autocomplete
} from '@mui/material';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Layout,
  Boxes,
  Zap,
  Plug,
  Database,
  Server,
  Palette,
  Clock,
  Check,
  Circle,
  Edit3,
  FileText,
  CheckCircle,
  XCircle,
  RotateCcw,
  X,
  FolderOpen,
  User,
  Link2,
  Search,
  BookOpen
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import QuoteItemEditor from './QuoteItemEditor';
import DependencySelector from './DependencySelector';
import ActualHoursModal from './ActualHoursModal';
import DocsActionDialog from './DocsActionDialog';

const sectionTypeIcons = {
  PAGES: <Layout size={18} />,
  COMPONENTS: <Boxes size={18} />,
  FEATURES: <Zap size={18} />,
  INTEGRATIONS: <Plug size={18} />,
  DATABASE: <Database size={18} />,
  INFRASTRUCTURE: <Server size={18} />,
  DESIGN: <Palette size={18} />
};

const sectionTypeLabels = {
  PAGES: 'Pages / Écrans',
  COMPONENTS: 'Composants UI',
  FEATURES: 'Fonctionnalités',
  INTEGRATIONS: 'Intégrations',
  DATABASE: 'Base de données',
  INFRASTRUCTURE: 'Infrastructure',
  DESIGN: 'Design / UX'
};

const statusColors = {
  TODO: { color: '#9ca3af', label: 'À faire', icon: <Circle size={14} /> },
  IN_PROGRESS: { color: '#f59e0b', label: 'En cours', icon: <Clock size={14} /> },
  DONE: { color: '#10b981', label: 'Terminé', icon: <Check size={14} /> }
};

function SortableSection({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {typeof children === 'function' ? children(listeners) : children}
    </div>
  );
}

function QuoteBuilder({ submissionId, isAdmin = false, isInternalUser = false, isClient = false, onRefresh }) {
  const [sections, setSections] = useState([]);
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [addSectionDialogOpen, setAddSectionDialogOpen] = useState(false);
  const [newSection, setNewSection] = useState({ type: 'PAGES', categoryId: '', name: '', isOptional: false, isIncluded: false });
  const [editingItem, setEditingItem] = useState(null);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  // Project type configuration (dynamic categories, hour types)
  const [projectType, setProjectType] = useState(null);
  const [categories, setCategories] = useState([]);
  const [hourTypes, setHourTypes] = useState([]);
  // Assignment system
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assignPopover, setAssignPopover] = useState({ anchor: null, targetType: null, targetId: null });
  // Dependency system
  const [dependencyDialog, setDependencyDialog] = useState({ 
    open: false, 
    sourceType: null, // 'section' | 'item'
    sourceId: null, 
    sourceSectionId: null,
    currentDependency: null // { type, id } or null
  });
  // Annexe PDF toggle
  const [includeAnnexe, setIncludeAnnexe] = useState(true);
  // Item estimates from DB
  const [itemEstimates, setItemEstimates] = useState([]);
  const [addItemDialog, setAddItemDialog] = useState({ open: false, sectionId: null, categoryId: null });
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [estimateCategoryFilter, setEstimateCategoryFilter] = useState('');
  // Actual hours modal (when completing an item)
  const [actualHoursModal, setActualHoursModal] = useState({ open: false, item: null });
  const { socket } = useSocket();
  const navigate = useNavigate();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      
      // Fetch submission to get projectType with its configs
      const submissionRes = await fetch(`/api/submissions/${submissionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let projectTypeId = null;
      if (submissionRes.ok) {
        const submissionData = await submissionRes.json();
        if (submissionData.includeAnnexe !== undefined) {
          setIncludeAnnexe(submissionData.includeAnnexe);
        }
        if (submissionData.projectType) {
          setProjectType(submissionData.projectType);
          setCategories(submissionData.projectType.categories || []);
          setHourTypes(submissionData.projectType.hourTypes || []);
          projectTypeId = submissionData.projectType.id;
        }
      }
      
      // Fetch item estimates for this project type
      if (projectTypeId) {
        const estimatesRes = await fetch(`/api/settings/item-estimates?projectTypeId=${projectTypeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (estimatesRes.ok) {
          const estimatesData = await estimatesRes.json();
          setItemEstimates(estimatesData);
        }
      }

      // Fetch sections
      const sectionsRes = await fetch(`/api/submissions/${submissionId}/quote/sections`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (sectionsRes.ok) {
        const data = await sectionsRes.json();
        setSections(data);
        // Expand all sections by default
        const expanded = {};
        data.forEach(s => { expanded[s.id] = true; });
        setExpandedSections(expanded);
      }

      // Legacy: Fetch hourly rates as fallback for old sections without category
      const ratesRes = await fetch('/api/settings/hourly-rates', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (ratesRes.ok) {
        const ratesData = await ratesRes.json();
        const rateMap = {};
        ratesData.forEach(r => { rateMap[r.sectionType] = r.rate; });
        setRates(rateMap);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    }
    setLoading(false);
  };

  const fetchAssignedUsers = async () => {
    if (!isInternalUser) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${submissionId}/assignments/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignedUsers(data);
      }
    } catch (err) {
      console.error('Fetch assigned users error:', err);
    }
  };

  const handleToggleAnnexe = async (checked) => {
    setIncludeAnnexe(checked);
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/submissions/${submissionId}/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ includeAnnexe: checked })
      });
    } catch (err) {
      console.error('Toggle annexe error:', err);
      setIncludeAnnexe(!checked); // Revert on error
    }
  };

  useEffect(() => {
    fetchData();
    fetchAssignedUsers();
  }, [submissionId]);

  // Listen to WebSocket events for quote changes
  useEffect(() => {
    if (!socket) return;

    const handleQuoteChanged = (data) => {
      if (data.submissionId === submissionId) {
        fetchData();
        if (onRefresh) onRefresh();
      }
    };

    const handleAssignmentChanged = (data) => {
      if (data.submissionId === submissionId) {
        fetchAssignedUsers();
      }
    };

    socket.on('quoteChanged', handleQuoteChanged);
    socket.on('assignmentChanged', handleAssignmentChanged);
    return () => {
      socket.off('quoteChanged', handleQuoteChanged);
      socket.off('assignmentChanged', handleAssignmentChanged);
    };
  }, [socket, submissionId, onRefresh]);

  const handleAddSection = async () => {
    if (!newSection.name.trim()) return;
    
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${submissionId}/quote/sections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newSection)
      });
      
      if (response.ok) {
        setNewSection({ type: 'PAGES', name: '' });
        setAddSectionDialogOpen(false);
        fetchData();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error('Add section error:', err);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Supprimer cette section et tous ses éléments ?')) return;
    
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/quote/sections/${sectionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Delete section error:', err);
    }
  };

  // Assignment functions
  const handleAssign = async (targetType, targetId, userId) => {
    try {
      const token = localStorage.getItem('portalToken');
      const url = targetType === 'section' 
        ? `/api/quote/sections/${targetId}`
        : `/api/quote/items/${targetId}`;
      
      await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ assignedToId: userId || null })
      });
      
      setAssignPopover({ anchor: null, targetType: null, targetId: null });
      fetchData();
    } catch (err) {
      console.error('Assignment error:', err);
    }
  };

  const handleUpdateDates = async (targetType, targetId, field, value) => {
    try {
      const token = localStorage.getItem('portalToken');
      const url = targetType === 'section' 
        ? `/api/quote/sections/${targetId}`
        : `/api/quote/items/${targetId}`;
      
      await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [field]: value || null })
      });
      
      fetchData();
    } catch (err) {
      console.error('Update dates error:', err);
    }
  };

  // Handle dependency selection from DependencySelector dialog
  const handleDependencySelect = async (dependency) => {
    // dependency is { type: 'section'|'item', id: string } or null
    const { sourceType, sourceId } = dependencyDialog;
    
    try {
      const token = localStorage.getItem('portalToken');
      const url = sourceType === 'section' 
        ? `/api/quote/sections/${sourceId}`
        : `/api/quote/items/${sourceId}`;
      
      await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          dependsOnType: dependency?.type || null,
          dependsOnId: dependency?.id || null 
        })
      });
      
      fetchData();
    } catch (err) {
      console.error('Update dependency error:', err);
    }
  };

  const handleUpdateDependency = async (targetType, targetId, dependsOnType, dependsOnId) => {
    try {
      const token = localStorage.getItem('portalToken');
      const url = targetType === 'section' 
        ? `/api/quote/sections/${targetId}`
        : `/api/quote/items/${targetId}`;
      
      await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          dependsOnType: dependsOnType || null,
          dependsOnId: dependsOnId || null 
        })
      });
      
      fetchData();
    } catch (err) {
      console.error('Update dependency error:', err);
    }
  };

  const handleOpenAddItemDialog = (sectionId, categoryId) => {
    setSelectedEstimate(null);
    setAddItemDialog({ open: true, sectionId, categoryId });
  };

  const handleAddItemFromDialog = async (useEstimate = false) => {
    const { sectionId } = addItemDialog;
    if (!sectionId) return;

    try {
      const token = localStorage.getItem('portalToken');
      
      // Build item data
      let itemData = { name: 'Nouvel élément' };
      
      if (useEstimate && selectedEstimate) {
        // Pre-fill from estimate
        itemData = {
          name: selectedEstimate.name,
          description: selectedEstimate.description || '',
          complexity: selectedEstimate.complexity || 'MEDIUM',
          // Link to ItemEstimate for average calculation
          itemEstimateId: selectedEstimate.id
        };
        
        // Convert hoursBreakdown to the hours format expected by API
        if (selectedEstimate.hoursBreakdown && hourTypes.length > 0) {
          const hoursData = {};
          for (const ht of hourTypes) {
            const value = selectedEstimate.hoursBreakdown[ht.slug] || 0;
            if (value > 0) {
              hoursData[ht.id] = value;
            }
          }
          if (Object.keys(hoursData).length > 0) {
            itemData.hours = hoursData;
          }
        }
      }
      
      const response = await fetch(`/api/quote/sections/${sectionId}/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(itemData)
      });
      
      if (response.ok) {
        const newItem = await response.json();
        fetchData();
        // Only open editor if it's a new blank item
        if (!useEstimate) {
          setEditingItem(newItem.id);
        }
      }
    } catch (err) {
      console.error('Add item error:', err);
    }
    
    setAddItemDialog({ open: false, sectionId: null, categoryId: null });
    setSelectedEstimate(null);
    setEstimateCategoryFilter('');
  };

  const handleUpdateItem = async (itemId, data) => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/quote/items/${itemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Update item error:', err);
    }
  };

  // Handle status change - opens modal when changing to DONE
  const handleStatusChange = (item, newStatus) => {
    if (newStatus === 'DONE' && item.status !== 'DONE') {
      // Open actual hours modal
      setActualHoursModal({ open: true, item });
    } else {
      // Direct update for other status changes
      handleUpdateItem(item.id, { status: newStatus });
    }
  };

  // Handle actual hours modal save
  const handleActualHoursSave = (data) => {
    if (actualHoursModal.item) {
      handleUpdateItem(actualHoursModal.item.id, data);
    }
    setActualHoursModal({ open: false, item: null });
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/quote/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Delete item error:', err);
    }
  };

  // Update section (for optional/included toggles)
  const handleUpdateSection = async (sectionId, data) => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/quote/sections/${sectionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Update section error:', err);
    }
  };

  // Accept/decline optional section
  const handleAcceptSection = async (sectionId, accepted) => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/quote/sections/${sectionId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ accepted })
      });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Accept section error:', err);
    }
  };

  // Reset section acceptance (admin only)
  const handleResetSection = async (sectionId) => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/quote/sections/${sectionId}/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Reset section error:', err);
    }
  };

  // Accept/decline optional item
  const handleAcceptItem = async (itemId, accepted) => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/quote/items/${itemId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ accepted })
      });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Accept item error:', err);
    }
  };

  // Reset item acceptance (admin only)
  const handleResetItem = async (itemId) => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/quote/items/${itemId}/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Reset item error:', err);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const allExpanded = sections.length > 0 && sections.every(s => expandedSections[s.id]);

  const toggleAllSections = () => {
    const newState = !allExpanded;
    const updated = {};
    sections.forEach(s => { updated[s.id] = newState; });
    setExpandedSections(updated);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex(s => s.id === active.id);
    const newIndex = sections.findIndex(s => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistic reorder
    const reordered = [...sections];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    setSections(reordered);

    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${submissionId}/quote/sections/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sectionIds: reordered.map(s => s.id) })
      });
      if (!response.ok) throw new Error('Reorder failed');
    } catch (err) {
      console.error('Reorder error:', err);
      fetchData(); // Rollback
    }
  };

  const calculateSectionTotal = (section) => {
    // Use frozen rate if available, then category hourlyRate, fallback to legacy rates
    const rate = section.frozenHourlyRate ?? section.category?.hourlyRate ?? rates[section.type] ?? 150;
    let totalHours = 0;
    
    section.items?.forEach(item => {
      // Dynamic hours from QuoteItemHour
      if (item.hours && item.hours.length > 0) {
        totalHours += item.hours.reduce((sum, h) => sum + (h.hours || 0), 0);
      }
    });
    
    return {
      hours: totalHours,
      price: totalHours * rate,
      rate
    };
  };

  const calculateProgress = (section) => {
    if (!section.items?.length) return 0;
    const done = section.items.filter(i => i.status === 'DONE').length;
    return (done / section.items.length) * 100;
  };

  if (loading) {
    return <Typography sx={{ py: 2, color: 'text.secondary' }}>Chargement du devis...</Typography>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          Détail du devis
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {sections.length > 0 && (
            <Tooltip title={allExpanded ? 'Tout réduire' : 'Tout étendre'}>
              <IconButton size="small" onClick={toggleAllSections}>
                {allExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </IconButton>
            </Tooltip>
          )}
          {isAdmin && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includeAnnexe}
                  onChange={(e) => handleToggleAnnexe(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BookOpen size={14} />
                  Annexe PDF
                </Typography>
              }
              sx={{ mr: 1 }}
            />
          )}
          {isInternalUser && sections.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileText size={16} />}
              onClick={() => setDocsDialogOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Documentation
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={() => setAddSectionDialogOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Section
            </Button>
          )}
        </Stack>
      </Box>

      {/* Sections */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
      <Stack spacing={2}>
        {sections.map((section) => {
          const totals = calculateSectionTotal(section);
          const progress = calculateProgress(section);
          const isExpanded = expandedSections[section.id];

          return (
            <SortableSection key={section.id} id={section.id} disabled={!isAdmin}>
              {(dragListeners) => (
            <Paper
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  bgcolor: 'rgba(0, 212, 170, 0.03)',
                  borderBottom: isExpanded ? '1px solid rgba(255, 255, 255, 0.06)' : 'none',
                  '&:hover': { bgcolor: 'rgba(0, 212, 170, 0.06)' }
                }}
                onClick={() => toggleSection(section.id)}
              >
                {isAdmin && (
                  <Box
                    component="span"
                    {...dragListeners}
                    onClick={e => e.stopPropagation()}
                    sx={{ display: 'flex', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                  >
                    <GripVertical size={16} color="#6b7280" />
                  </Box>
                )}
                
                <IconButton size="small" sx={{ p: 0 }}>
                  {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </IconButton>

                <Box sx={{ color: '#00d4aa', display: 'flex' }}>
                  {sectionTypeIcons[section.type]}
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {section.name}
                    </Typography>
                    {section.isIncluded && (
                      <Chip label="Offert" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontWeight: 600 }} />
                    )}
                    {section.isOptional && (
                      <Chip label="Optionnel" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', fontWeight: 600 }} />
                    )}
                    {section.isOptional && section.isAcceptedByClient === true && (
                      <Chip icon={<CheckCircle size={12} />} label="Accepté" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontWeight: 600, '& .MuiChip-icon': { color: '#10b981' } }} />
                    )}
                    {section.isOptional && section.isAcceptedByClient === false && (
                      <Chip icon={<XCircle size={12} />} label="Refusé" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', fontWeight: 600, '& .MuiChip-icon': { color: '#ef4444' } }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {sectionTypeLabels[section.type]} • {section.items?.length || 0} éléments
                  </Typography>
                </Box>

                {/* Section Stats */}
                <Stack direction="row" spacing={1} alignItems="center" onClick={e => e.stopPropagation()}>
                  <Tooltip title="Progression">
                    <Box sx={{ width: 60 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.05)',
                          '& .MuiLinearProgress-bar': { bgcolor: '#00d4aa' }
                        }}
                      />
                    </Box>
                  </Tooltip>
                  
                  <Chip
                    label={section.isIncluded ? 'Offert' : `${totals.hours}h`}
                    size="small"
                    sx={{ bgcolor: section.isIncluded ? 'rgba(16, 185, 129, 0.1)' : 'rgba(0, 212, 170, 0.1)', color: section.isIncluded ? '#10b981' : '#00d4aa', fontWeight: 600 }}
                  />
                  
                  {!section.isIncluded && (
                    <Chip
                      label={`$${totals.price.toLocaleString()}`}
                      size="small"
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', fontWeight: 600 }}
                    />
                  )}

                  {/* Client accept/decline buttons for optional sections */}
                  {section.isOptional && !isAdmin && section.isAcceptedByClient !== true && section.isAcceptedByClient !== false && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={(e) => { e.stopPropagation(); handleAcceptSection(section.id, true); }}
                        sx={{ minWidth: 'auto', px: 1, py: 0.5, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, fontSize: '0.7rem' }}
                        startIcon={<Check size={12} />}
                      >
                        Accepter
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => { e.stopPropagation(); handleAcceptSection(section.id, false); }}
                        sx={{ minWidth: 'auto', px: 1, py: 0.5, borderColor: '#ef4444', color: '#ef4444', '&:hover': { borderColor: '#dc2626', bgcolor: 'rgba(239, 68, 68, 0.1)' }, fontSize: '0.7rem' }}
                        startIcon={<X size={12} />}
                      >
                        Refuser
                      </Button>
                    </>
                  )}

                  {/* Documents button - visible for sections with attachments */}
                  {section.attachments && section.attachments.length > 0 && (
                    <Tooltip title={`${section.attachments.length} document(s) associé(s)`}>
                      <IconButton 
                        size="small" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          const basePath = isAdmin || isInternalUser ? '/admin' : '/portal';
                          navigate(`${basePath}/submissions/${submissionId}/documents?section=${section.id}`);
                        }}
                        sx={{ color: '#3b82f6' }}
                      >
                        <FolderOpen size={14} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Assignment indicator - for internal users */}
                  {isInternalUser && (
                    <Tooltip title={section.assignedTo ? `Assigné à ${section.assignedTo.firstName || section.assignedTo.email}` : 'Assigner'}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setAssignPopover({ anchor: e.currentTarget, targetType: 'section', targetId: section.id }); }}
                        sx={{ 
                          p: 0.25,
                          border: section.assignedTo ? '2px solid #00d4aa' : '2px dashed rgba(255,255,255,0.2)',
                          borderRadius: '50%'
                        }}
                      >
                        {section.assignedTo ? (
                          <Avatar sx={{ width: 20, height: 20, bgcolor: '#00d4aa20', color: '#00d4aa', fontSize: '0.65rem' }}>
                            {section.assignedTo.firstName?.[0] || section.assignedTo.email[0].toUpperCase()}
                          </Avatar>
                        ) : (
                          <User size={14} color="#666" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Dependency indicator - for internal users */}
                  {isInternalUser && (
                    <Tooltip title={section.dependsOn ? `Prérequis: ${section.dependsOn.type === 'section' ? 'Section' : 'Élément'}: ${section.dependsOn.name}` : 'Définir un prérequis'}>
                      <IconButton
                        size="small"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setDependencyDialog({ 
                            open: true, 
                            sourceType: 'section', 
                            sourceId: section.id, 
                            sourceSectionId: null,
                            currentDependency: section.dependsOn ? { type: section.dependsOn.type, id: section.dependsOn.id } : null
                          }); 
                        }}
                        sx={{ 
                          p: 0.25,
                          border: section.dependsOn ? '2px solid #f59e0b' : '2px dashed rgba(255,255,255,0.2)',
                          borderRadius: '50%'
                        }}
                      >
                        <Link2 size={14} color={section.dependsOn ? '#f59e0b' : '#666'} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Admin controls */}
                  {isAdmin && (
                    <>
                      {/* Reset button for decided optional sections */}
                      {section.isOptional && (section.isAcceptedByClient === true || section.isAcceptedByClient === false) && (
                        <Tooltip title="Réinitialiser la décision">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleResetSection(section.id); }} sx={{ color: '#8b5cf6' }}>
                            <RotateCcw size={14} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Edit section toggles */}
                      <Tooltip title="Modifier optionnel/offert">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingSectionId(editingSectionId === section.id ? null : section.id); }}>
                          <Edit3 size={14} />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }}
                        sx={{ color: '#ef4444' }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </>
                  )}
                </Stack>
              </Box>

              {/* Section Edit Toggles (Admin) */}
              {isAdmin && editingSectionId === section.id && (
                <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(139, 92, 246, 0.05)', borderBottom: '1px solid rgba(139, 92, 246, 0.1)', display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Le nom de section n'etait editable nulle part alors que
                      l'API l'accepte depuis toujours. */}
                  <TextField
                    size="small"
                    label="Nom de la section"
                    defaultValue={section.name}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => {
                      const valeur = e.target.value.trim();
                      if (valeur && valeur !== section.name) handleUpdateSection(section.id, { name: valeur });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.target.blur();
                      if (e.key === 'Escape') { e.target.value = section.name; e.target.blur(); }
                    }}
                    sx={{ minWidth: 260 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={section.isIncluded || false}
                        onChange={(e) => handleUpdateSection(section.id, { isIncluded: e.target.checked, isOptional: e.target.checked ? false : section.isOptional })}
                        color="success"
                      />
                    }
                    label={<Typography variant="body2" sx={{ color: section.isIncluded ? '#10b981' : 'inherit' }}>Heures offertes</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={section.isOptional || false}
                        onChange={(e) => handleUpdateSection(section.id, { isOptional: e.target.checked, isIncluded: e.target.checked ? false : section.isIncluded })}
                        color="warning"
                      />
                    }
                    label={<Typography variant="body2" sx={{ color: section.isOptional ? '#f59e0b' : 'inherit' }}>Optionnel</Typography>}
                  />
                  <Button size="small" onClick={() => setEditingSectionId(null)}>Fermer</Button>
                </Box>
              )}

              {/* Section Items */}
              <Collapse in={isExpanded}>
                <Box sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    {section.items?.map((item) => {
                      // Calculate total hours from dynamic QuoteItemHour
                      const itemTotalHours = item.hours?.reduce((sum, h) => sum + (h.hours || 0), 0) || 0;

                      return (
                        <Paper
                          key={item.id}
                          sx={{
                            p: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.01)',
                            border: '1px solid rgba(255, 255, 255, 0.04)',
                            borderRadius: 1.5,
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.03)',
                              borderColor: 'rgba(0, 212, 170, 0.2)'
                            }
                          }}
                        >
                          {editingItem === item.id ? (
                            <QuoteItemEditor
                              item={item}
                              hourTypes={hourTypes}
                              onSave={(data) => {
                                handleUpdateItem(item.id, data);
                                setEditingItem(null);
                              }}
                              onCancel={() => setEditingItem(null)}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              {isAdmin && <GripVertical size={14} color="#6b7280" style={{ marginTop: 4, cursor: 'grab' }} />}
                              
                              <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {item.name}
                                  </Typography>
                                  {item.complexity && (
                                    <Chip
                                      label={item.complexity}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        bgcolor: 'rgba(255, 255, 255, 0.05)'
                                      }}
                                    />
                                  )}
                                  {item.isIncluded && (
                                    <Chip label="Offert" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontWeight: 600 }} />
                                  )}
                                  {item.isOptional && (
                                    <Chip label="Optionnel" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', fontWeight: 600 }} />
                                  )}
                                  {item.isOptional && item.isAcceptedByClient === true && (
                                    <Chip icon={<CheckCircle size={10} />} label="Accepté" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontWeight: 600, '& .MuiChip-icon': { color: '#10b981' } }} />
                                  )}
                                  {item.isOptional && item.isAcceptedByClient === false && (
                                    <Chip icon={<XCircle size={10} />} label="Refusé" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', fontWeight: 600, '& .MuiChip-icon': { color: '#ef4444' } }} />
                                  )}
                                </Box>
                                
                                {item.description && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {item.description}
                                  </Typography>
                                )}

                                {/* Hours breakdown - dynamic hours from QuoteItemHour */}
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {item.hours?.filter(h => h.hours > 0).map((h, idx) => (
                                    <Chip 
                                      key={h.hourTypeId || idx}
                                      label={`${h.hourType?.name || 'H'}: ${h.hours}h`} 
                                      size="small" 
                                      sx={{ 
                                        height: 20, 
                                        fontSize: '0.65rem', 
                                        bgcolor: 'rgba(0, 212, 170, 0.1)', 
                                        color: '#00d4aa' 
                                      }} 
                                    />
                                  ))}
                                </Stack>
                              </Box>

                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: 600, color: item.isIncluded ? '#10b981' : '#00d4aa' }}>
                                  {item.isIncluded ? 'Offert' : `${itemTotalHours}h`}
                                </Typography>

                                {/* Client accept/decline buttons for optional items */}
                                {item.isOptional && !isAdmin && item.isAcceptedByClient !== true && item.isAcceptedByClient !== false && (
                                  <>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleAcceptItem(item.id, true)}
                                      sx={{ minWidth: 'auto', px: 1, py: 0.25, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, fontSize: '0.65rem' }}
                                      startIcon={<Check size={10} />}
                                    >
                                      Accepter
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleAcceptItem(item.id, false)}
                                      sx={{ minWidth: 'auto', px: 1, py: 0.25, borderColor: '#ef4444', color: '#ef4444', '&:hover': { borderColor: '#dc2626', bgcolor: 'rgba(239, 68, 68, 0.1)' }, fontSize: '0.65rem' }}
                                      startIcon={<X size={10} />}
                                    >
                                      Refuser
                                    </Button>
                                  </>
                                )}

                                {isAdmin ? (
                                  <Select
                                    size="small"
                                    value={item.status}
                                    onChange={(e) => handleStatusChange(item, e.target.value)}
                                    sx={{ minWidth: 110, height: 28, fontSize: '0.7rem' }}
                                  >
                                    {Object.entries(statusColors).map(([status, { label, icon, color }]) => (
                                      <MenuItem key={status} value={status} sx={{ fontSize: '0.75rem' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color }}>
                                          {icon}<span>{label}</span>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                ) : (
                                  <Chip
                                    icon={statusColors[item.status].icon}
                                    label={statusColors[item.status].label}
                                    size="small"
                                    sx={{
                                      bgcolor: `${statusColors[item.status].color}15`,
                                      color: statusColors[item.status].color,
                                      fontSize: '0.7rem'
                                    }}
                                    variant="outlined"
                                  />
                                )}

                                {/* Assignment indicator for items - internal users only - AFTER status */}
                                {isInternalUser && (
                                  <Tooltip title={item.assignedTo ? `Assigné à ${item.assignedTo.firstName || item.assignedTo.email}` : 'Assigner'}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); setAssignPopover({ anchor: e.currentTarget, targetType: 'item', targetId: item.id }); }}
                                      sx={{ 
                                        p: 0.25,
                                        border: item.assignedTo ? '2px solid #00d4aa' : '2px dashed rgba(255,255,255,0.2)',
                                        borderRadius: '50%'
                                      }}
                                    >
                                      {item.assignedTo ? (
                                        <Avatar sx={{ width: 18, height: 18, bgcolor: '#00d4aa20', color: '#00d4aa', fontSize: '0.6rem' }}>
                                          {item.assignedTo.firstName?.[0] || item.assignedTo.email[0].toUpperCase()}
                                        </Avatar>
                                      ) : (
                                        <User size={12} color="#666" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                )}

                                {/* Dependency indicator for items - internal users only */}
                                {isInternalUser && (
                                  <Tooltip title={item.dependsOn ? `Prérequis: ${item.dependsOn.type === 'section' ? 'Section' : 'Élément'}: ${item.dependsOn.sectionName ? `${item.dependsOn.sectionName} > ` : ''}${item.dependsOn.name}` : 'Définir un prérequis'}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { 
                                        e.stopPropagation(); 
                                        setDependencyDialog({ 
                                          open: true, 
                                          sourceType: 'item', 
                                          sourceId: item.id, 
                                          sourceSectionId: section.id,
                                          currentDependency: item.dependsOn ? { type: item.dependsOn.type, id: item.dependsOn.id } : null
                                        }); 
                                      }}
                                      sx={{ 
                                        p: 0.25,
                                        border: item.dependsOn ? '2px solid #f59e0b' : '2px dashed rgba(255,255,255,0.2)',
                                        borderRadius: '50%'
                                      }}
                                    >
                                      <Link2 size={12} color={item.dependsOn ? '#f59e0b' : '#666'} />
                                    </IconButton>
                                  </Tooltip>
                                )}

                                {isAdmin && (
                                  <>
                                    {/* Reset button for decided optional items */}
                                    {item.isOptional && (item.isAcceptedByClient === true || item.isAcceptedByClient === false) && (
                                      <Tooltip title="Réinitialiser">
                                        <IconButton size="small" onClick={() => handleResetItem(item.id)} sx={{ color: '#8b5cf6' }}>
                                          <RotateCcw size={12} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <IconButton size="small" onClick={() => setEditingItem(item.id)}>
                                      <Edit3 size={14} />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleDeleteItem(item.id)} sx={{ color: '#ef4444' }}>
                                      <Trash2 size={14} />
                                    </IconButton>
                                  </>
                                )}
                              </Stack>
                            </Box>
                          )}
                        </Paper>
                      );
                    })}

                    {section.items?.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        Aucun élément dans cette section
                      </Typography>
                    )}

                    {isAdmin && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Plus size={14} />}
                        onClick={() => handleOpenAddItemDialog(section.id, section.category?.id)}
                        sx={{ alignSelf: 'flex-start', borderStyle: 'dashed' }}
                      >
                        Ajouter un élément
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Collapse>
            </Paper>
              )}
            </SortableSection>
          );
        })}

        {sections.length === 0 && (
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.01)',
              border: '1px dashed rgba(255, 255, 255, 0.1)',
              borderRadius: 2
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Aucune section de devis définie
            </Typography>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setAddSectionDialogOpen(true)}
              >
                Ajouter une section
              </Button>
            )}
          </Paper>
        )}
      </Stack>
        </SortableContext>
      </DndContext>

      {/* Add Section Dialog */}
      <Dialog
        open={addSectionDialogOpen}
        onClose={() => setAddSectionDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Nouvelle section</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Catégorie</InputLabel>
              <Select
                value={categories.length > 0 ? newSection.categoryId : newSection.type}
                label="Catégorie"
                onChange={(e) => {
                  if (categories.length > 0) {
                    setNewSection({ ...newSection, categoryId: e.target.value, type: null });
                  } else {
                    setNewSection({ ...newSection, type: e.target.value, categoryId: '' });
                  }
                }}
              >
                {categories.length > 0 
                  ? categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {cat.icon ? sectionTypeIcons[cat.icon] || <Layout size={18} /> : <Layout size={18} />}
                          <span>{cat.name}</span>
                        </Box>
                      </MenuItem>
                    ))
                  : Object.entries(sectionTypeLabels).map(([type, label]) => (
                      <MenuItem key={type} value={type}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {sectionTypeIcons[type]}
                          <span>{label}</span>
                        </Box>
                      </MenuItem>
                    ))
                }
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Nom de la section"
              placeholder="Ex: Pages publiques, Module utilisateurs..."
              value={newSection.name}
              onChange={(e) => setNewSection({ ...newSection, name: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setAddSectionDialogOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSection}
            disabled={!newSection.name.trim()}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assignment Popover */}
      <Popover
        open={Boolean(assignPopover.anchor)}
        anchorEl={assignPopover.anchor}
        onClose={() => setAssignPopover({ anchor: null, targetType: null, targetId: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minWidth: 200, maxHeight: 300 } }}
      >
        <List dense sx={{ py: 0.5 }}>
          <ListItemButton
            onClick={() => handleAssign(assignPopover.targetType, assignPopover.targetId, null)}
            sx={{ py: 1 }}
          >
            <ListItemAvatar sx={{ minWidth: 36 }}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'rgba(255,255,255,0.1)' }}>
                <User size={14} />
              </Avatar>
            </ListItemAvatar>
            <ListItemText 
              primary="Non assigné" 
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
          </ListItemButton>
          <Divider sx={{ my: 0.5 }} />
          {assignedUsers.map((u) => (
            <ListItemButton
              key={u.id}
              onClick={() => handleAssign(assignPopover.targetType, assignPopover.targetId, u.id)}
              sx={{ py: 1 }}
            >
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <Avatar sx={{ width: 24, height: 24, bgcolor: '#00d4aa20', color: '#00d4aa', fontSize: '0.7rem' }}>
                  {u.firstName?.[0] || u.email[0].toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary={`${u.firstName || ''} ${u.lastName || u.email}`}
                secondary={u.role}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItemButton>
          ))}
          {assignedUsers.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block', textAlign: 'center' }}>
              Aucun membre assigné au projet
            </Typography>
          )}
        </List>
      </Popover>

      {/* Dependency Selector Dialog */}
      <DependencySelector
        open={dependencyDialog.open}
        onClose={() => setDependencyDialog({ open: false, sourceType: null, sourceId: null, sourceSectionId: null, currentDependency: null })}
        onSelect={handleDependencySelect}
        sections={sections}
        sourceType={dependencyDialog.sourceType}
        sourceId={dependencyDialog.sourceId}
        sourceSectionId={dependencyDialog.sourceSectionId}
        currentDependency={dependencyDialog.currentDependency}
      />

      {/* Add Item Dialog - Choose from estimates or create new */}
      <Dialog
        open={addItemDialog.open}
        onClose={() => { setAddItemDialog({ open: false, sectionId: null, categoryId: null }); setSelectedEstimate(null); setEstimateCategoryFilter(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Plus size={20} color="#00d4aa" />
          Ajouter un élément
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Estimate Selector with manual category filter */}
            {itemEstimates.length > 0 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    DEPUIS LA BIBLIOTHÈQUE ({itemEstimates.length} éléments)
                  </Typography>
                  
                  {/* Category Filter */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select
                      value={estimateCategoryFilter}
                      onChange={(e) => setEstimateCategoryFilter(e.target.value)}
                      displayEmpty
                      sx={{ fontSize: '0.75rem' }}
                    >
                      <MenuItem value="">
                        <em>Toutes les catégories</em>
                      </MenuItem>
                      {categories.map(cat => (
                        <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                
                <Autocomplete
                  options={
                    estimateCategoryFilter 
                      ? itemEstimates.filter(e => e.categoryId === estimateCategoryFilter || e.category?.id === estimateCategoryFilter)
                      : itemEstimates
                  }
                  value={selectedEstimate}
                  onChange={(_, newValue) => setSelectedEstimate(newValue)}
                  getOptionLabel={(option) => option.name}
                  groupBy={(option) => option.category?.name || 'Général'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Rechercher un élément..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <Search size={16} style={{ marginRight: 8, color: '#6b7280' }} />
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const totalHours = option.hoursBreakdown 
                      ? Object.values(option.hoursBreakdown).reduce((sum, h) => sum + (h || 0), 0)
                      : 0;
                    return (
                      <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.name}
                          </Typography>
                          {option.category?.name && (
                            <Chip 
                              label={option.category.name} 
                              size="small" 
                              sx={{ 
                                height: 18, 
                                fontSize: '0.65rem',
                                bgcolor: 'rgba(0, 212, 170, 0.15)',
                                color: '#00d4aa'
                              }} 
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {option.complexity} • {totalHours}h
                          {option.description && ` • ${option.description.slice(0, 50)}${option.description.length > 50 ? '...' : ''}`}
                        </Typography>
                      </Box>
                    );
                  }}
                  sx={{ mb: 1 }}
                />
                
                {selectedEstimate && (
                  <Paper sx={{ p: 2, bgcolor: 'rgba(0, 212, 170, 0.05)', border: '1px solid rgba(0, 212, 170, 0.2)', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {selectedEstimate.name}
                    </Typography>
                    {selectedEstimate.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedEstimate.description}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2}>
                      <Chip 
                        size="small" 
                        label={selectedEstimate.complexity || 'MEDIUM'} 
                        sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {selectedEstimate.hoursBreakdown && Object.entries(selectedEstimate.hoursBreakdown)
                          .filter(([_, h]) => h > 0)
                          .map(([type, h]) => `${type}: ${h}h`)
                          .join(' • ')}
                      </Typography>
                    </Stack>
                  </Paper>
                )}
              </Box>
            )}

            <Divider sx={{ opacity: 0.2 }}>
              <Typography variant="caption" color="text.secondary">OU</Typography>
            </Divider>

            {/* Create New Option */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<Edit3 size={16} />}
                onClick={() => handleAddItemFromDialog(false)}
                sx={{ borderStyle: 'dashed' }}
              >
                Créer un élément libre
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => { setAddItemDialog({ open: false, sectionId: null, categoryId: null }); setSelectedEstimate(null); setEstimateCategoryFilter(''); }} 
            color="inherit"
          >
            Annuler
          </Button>
          {selectedEstimate && (
            <Button
              variant="contained"
              onClick={() => handleAddItemFromDialog(true)}
              startIcon={<BookOpen size={16} />}
            >
              Ajouter depuis la bibliothèque
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Documentation : génération IA, sections manquantes, correction de mise en forme */}
      <DocsActionDialog
        open={docsDialogOpen}
        onClose={() => setDocsDialogOpen(false)}
        submissionId={submissionId}
        sections={sections}
        onDone={() => { if (onRefresh) onRefresh(); fetchData(); }}
      />

      {/* Actual Hours Modal - shown when completing an item */}
      <ActualHoursModal
        open={actualHoursModal.open}
        onClose={() => setActualHoursModal({ open: false, item: null })}
        item={actualHoursModal.item}
        onSave={handleActualHoursSave}
      />
    </Box>
  );
}

export default QuoteBuilder;
